import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";

const FAMILIES = ["A320", "737", "787", "A350", "777", "A380", "747", "A330", "E190", "A220"];

function pick(items, term) {
  const t = term.toLowerCase();
  return items.find((a) => (a.name || "").toLowerCase().includes(t));
}

export default function PopularAircraft() {
  const [cards, setCards] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [article, setArticle] = useState(null);
  const [artLoading, setArtLoading] = useState(true);

  useEffect(() => {
    api.get("/aircraft").then((r) => {
      const list = r.data || [];
      const seen = new Set();
      const matched = [];
      for (const fam of FAMILIES) {
        const m = pick(list, fam);
        if (m && !seen.has(m.id)) { seen.add(m.id); matched.push(m); }
        if (matched.length >= 8) break;
      }
      setCards(matched);
      if (matched.length) {
        const idx = new Date().getDate() % matched.length;
        setFeatured(matched[idx]);
      }
    }).catch(() => setCards([]));
  }, []);

  useEffect(() => {
    if (!featured) return;
    const code = featured.iata || featured.icao || featured.id;
    setArtLoading(true); setArticle(null);
    api.get(`/aircraft/${encodeURIComponent(code)}/article`, { timeout: 65000 })
      .then((r) => setArticle(r.data)).catch(() => setArticle(null))
      .finally(() => setArtLoading(false));
  }, [featured]);

  if (cards && cards.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="home-popular-aircraft">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-700 text-2xl tracking-tight flex items-center gap-2"><Plane className="w-6 h-6 text-primary -rotate-45" /> Popular aircraft</h2>
        <Link to="/aircraft" className="text-sm text-primary hover:underline">All aircraft</Link>
      </div>

      {/* Featured aircraft of the day */}
      {featured && (
        <Link to={`/aircraft/${encodeURIComponent(featured.iata || featured.icao || featured.id)}`} data-testid="home-featured-aircraft"
          className="group block border border-zinc-300 bg-white p-6 lg:p-8 mb-4 hover:border-primary transition-colors">
          <div className="text-xs font-mono uppercase tracking-widest text-primary flex items-center gap-1.5 mb-2"><Sparkles className="w-3.5 h-3.5" /> Aircraft of the day</div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className="font-heading font-900 text-2xl sm:text-3xl group-hover:text-primary transition-colors">{featured.name}</h3>
            <span className="text-sm text-muted-foreground">{featured.manufacturer}</span>
          </div>
          {artLoading ? (
            <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Generating the AI profile…
            </div>
          ) : article ? (
            <>
              {article.tagline && <p className="mt-2 font-heading font-700 text-lg text-primary">{article.tagline}</p>}
              {article.overview && <p className="mt-2 text-base text-zinc-700 line-clamp-3">{article.overview}</p>}
            </>
          ) : null}
          <div className="mt-4 text-sm text-primary inline-flex items-center gap-1">Read full profile <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
        </Link>
      )}

      {/* Popular cards row */}
      {cards === null ? (
        <div className="h-24" />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {cards.map((a) => (
            <Link key={a.id} to={`/aircraft/${encodeURIComponent(a.iata || a.icao || a.id)}`} data-testid={`home-aircraft-${a.iata || a.id}`}
              className="group border border-zinc-300 bg-white p-4 flex items-center justify-between hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
              <div className="min-w-0">
                <div className="font-heading font-700 text-sm truncate">{a.name}</div>
                <div className="text-xs text-muted-foreground truncate">{a.manufacturer}</div>
              </div>
              {a.iata && <span className="font-mono text-primary text-sm shrink-0 ml-2">{a.iata}</span>}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
